/* eslint-disable react/prop-types */
import React, { Component, lazy } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import isLockAspect from '@editor/src/js/utils/lock-aspect';
import LazyBoundary from '@com/LazyBoundary';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { GroupUIEdge } from './element-types/group-element/constant';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { MenuManagerEventsKeys } from '@src/slide/application/service/menu-manager/menu-manager-events.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { CanvasElementPropType } from '@src/framework/editor/src/page/index/components/canvas/canvas-element';

const ResizableMobile = lazy(() => import(/* webpackChunkName: "lazyComponentsMobile" */ './resizable-mobile'));
const RotateMobile = lazy(() => import(/* webpackChunkName: "lazyComponentsMobile" */ './element-types/rotate/index-mobile'));
const DraggableMobileNew = lazy(() => import(/* webpackChunkName: "lazyComponentsMobile" */ './element-types/draggable-m'));
const SelectBorderMobile = lazy(() => import(/* webpackChunkName: "lazyComponentsMobile" */ './select-border-mobile'));
const MASK_COLOR = '#cccccc';
const MASK_OPACITY = 0.3;
const DEFAULT_SIZE = 0;

export const CanvasElementPropTypes = {
    onResize: PropTypes.func,
    onResizeStart: PropTypes.func,
    onResizeStop: PropTypes.func,
    onRotate: PropTypes.func,
    onRotateStart: PropTypes.func,
    onRotateStop: PropTypes.func,
    onBorderMouseDown: PropTypes.func,
    onBorderDoubleClick: PropTypes.func,
    onBorderMouseUp: PropTypes.func,
    draggable: PropTypes.bool,
    resizeHorizontal: PropTypes.bool,
    resizeVertical: PropTypes.bool,
    canRotate: PropTypes.bool,
    showSelectBorder: PropTypes.bool,
    borderDraggable: PropTypes.bool,
    scale: PropTypes.number,
    rotate: PropTypes.number,
    originX: PropTypes.number,
    originY: PropTypes.number,
    isDragging: PropTypes.bool,
    isSelected: PropTypes.bool,
    isResizing: PropTypes.bool,
    isRotating: PropTypes.bool,
    isCroping: PropTypes.bool,
    isMobileRotatingMode: PropTypes.bool,
    setIsMobileRotatingMode: PropTypes.func,
    selectedElementInGroupInMobile: PropTypes.bool,
    isPreview: PropTypes.bool,
    isPlaceholder: PropTypes.bool,
    editable: PropTypes.bool,
    getSize: PropTypes.func,
    rect: PropTypes.object,
    children: PropTypes.node,
    component: PropTypes.shape({
        props: PropTypes.object,
    }),
    style: PropTypes.object,
    relativeRotate: PropTypes.number,
    relativeFlipH: PropTypes.number,
    relativeFlipV: PropTypes.number,
    fromScale: PropTypes.number,
};

export interface CanvasElementMobilePropType extends CanvasElementPropType {
    isMobileRotatingMode?: boolean;
    setIsMobileRotatingMode?: (...arg: any[]) => any;
    selectedElementInGroupInMobile?: boolean;
    getFlip?: (...arg: any[]) => any;
    style: any;
}

@observer
class CanvasElementMobile extends Component<CanvasElementMobilePropType> {
    public static propTypes = CanvasElementPropTypes;

    public static contextTypes = {
    };

    public render() {
        if (!this.props.fromScale && this.props.component.currentEditState !== PPTEditorStateType.EDITABLE) {
            return this.renderWithoutEditable();
        }

        const rotateStyle = {
            ...this.props.style,
            width: '100%',
            height: '100%',
            zIndex: 99,
            pointerEvents: 'none',
        };

        return (
            <React.Fragment>
                <div onMouseEnter={this.handleMouseEnter} onMouseLeave={this.handleMouseLeave}>
                    {
                        this.renderResizableOrResizableMobile()
                    }
                    {
                        this.renderRotateOrDraggable(rotateStyle)
                    }
                    {
                        this.renderRotateMobile(rotateStyle)
                    }
                    {
                        this.renderMaskWrapper()
                    }
                    {
                        this.props.children
                    }
                    {
                        this.renderSelectBorder()
                    }
                </div>
            </React.Fragment>
        );
    }

    private getMenuManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.menuManager;
    }

    private handleMouseEnter = () => {
        if (this.shouldEmitEvent()) {
            this.getMenuManager()?.event.emit(MenuManagerEventsKeys.ENTER_ELEMENT, this.props.component.key);
        }
    };

    private handleMouseLeave = () => {
        if (this.shouldEmitEvent()) {
            this.getMenuManager()?.event.emit(MenuManagerEventsKeys.LEAVE_ELEMENT, this.props.component.key);
        }
    };

    private shouldEmitEvent = () => this.props.component.selected
        && !isIframeShapeModel(this.props.component);

    private shouldRenderBorderWithoutEditable = () => !this.props.isCroping
        && this.props.showSelectBorder
        && this.props.component.currentEditState <= PPTEditorStateType.SELECTABLE;

    private renderWithoutEditable() {
        const linkData = this.props.component.props.link;
        const data = linkData ? linkData.href : null;
        const href = data ? this.getLinkManager().getLinkUrlWithData(data) : undefined;
        return (
            <React.Fragment>
                <div
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                    title={href}>
                    {this.props.children}
                    {
                        this.shouldRenderBorderWithoutEditable()
                        && <LazyBoundary>
                            <SelectBorderMobile
                                scale={this.props.scale}
                                frame={this.props.rect}
                                borderDraggable={false}
                                onBorderMouseDown={this.props.editable === true
                                    ? this.props.onBorderMouseDown : undefined}
                                onBorderDoubleClick={this.props.editable === true
                                    ? this.props.onBorderDoubleClick : undefined}
                                isSelected={!!this.props.isSelected}
                                shouldRoundSize={this.props.component.type === UIModelType.SHAPE
                                || this.props.component.type === UIModelType.IMAGE}
                            />
                        </LazyBoundary>
                    }
                </div>
            </React.Fragment>
        );
    }

    private renderSelectBorder() {
        return this.shouldRenderBorderWithEditable()
            && <LazyBoundary>
                <SelectBorderMobile
                    scale={this.props.scale}
                    frame={this.props.rect}
                    frameOnSlide={this.props.rect}
                    onDrag={this.props.onDrag}
                    onDragStart={this.props.onDragStart}
                    onDragStop={this.props.onDragStop}
                    borderDraggable={!!this.props.borderDraggable}
                    onBorderMouseDown={this.props.editable === true
                        ? this.props.onBorderMouseDown : undefined}
                    onBorderDoubleClick={this.props.editable === true
                        ? this.props.onBorderDoubleClick : undefined}
                    onBorderMouseUp={this.props.editable === true
                        ? this.props.onBorderMouseUp : undefined}
                    isSelected={!!this.props.isSelected}
                    pointerEvents={'none'}
                    relativeRotate={this.props.relativeRotate}
                    relativeFlipH={this.props.relativeFlipH}
                    relativeFlipV={this.props.relativeFlipV}
                    shouldRoundSize={this.props.component.type === UIModelType.SHAPE
                    || this.props.component.type === UIModelType.IMAGE}
                />
            </LazyBoundary>;
    }

    // eslint-disable-next-line complexity
    private shouldRenderBorderWithEditable = () => {
        let shouldRender = !this.props.isCroping
            && this.props.showSelectBorder
            && !this.props.isPreview;
        shouldRender = shouldRender && !(!this.isEmptyText() && (this.props.isDragging || this.props.isRotating));
        return shouldRender;
    };

    private renderResizableMobile() {
        const isIframe = isIframeShapeModel(this.props.component);
        const resizable = this.canResizeOrDrag();

        return this.shouldRenderMobileResizable()
            ? <LazyBoundary>
                <ResizableMobile
                    getSize={this.props.getSize}
                    getFlip={this.props.getFlip}
                    onResize={this.props.onResize || (() => {})}
                    onResizeStart={this.props.onResizeStart || (() => {})}
                    onResizeStop={this.props.onResizeStop || (() => {})}
                    resizeHorizontal={resizable && !!this.props.resizeHorizontal}
                    resizeVertical={resizable && !!this.props.resizeVertical}
                    scale={this.props.scale}
                    rotate={this.props.rotate || 0}
                    isResizing={!!this.props.isResizing}
                    relativeRotate={this.props.relativeRotate}
                    relativeFlipH={this.props.relativeFlipH}
                    relativeFlipV={this.props.relativeFlipV}
                    isIframe={isIframe}
                    modelIsLockAspect={!!isLockAspect(this.props.component.type, this.props.component.props)}
                />
            </LazyBoundary>
            : null;
    }

    private shouldRenderResizable = () => {
        const shouldRender = this.props.isSelected
            && !this.props.isDragging
            && !this.props.isCroping
            && !this.props.isRotating
            && !this.props.isPreview;
        return shouldRender;
    };

    private shouldRenderMobileResizable = () => this.shouldRenderResizable()
        && !this.props.isMobileRotatingMode
        && !this.props.selectedElementInGroupInMobile;

    private renderRotateOrDraggable(rotateStyle) {
        return this.getDraggableMobile(rotateStyle);
    }

    private renderRotateMobile(rotateStyle) {
        return this.shouldRenderRotateMobile()
            ? <div style={rotateStyle}>
                <LazyBoundary>
                    <RotateMobile
                        scale={this.props.scale}
                        onRotate={this.props.onRotate}
                        onRotateStart={this.props.onRotateStart}
                        onRotateStop={this.props.onRotateStop}
                        rotate={this.props.rotate || 0}
                        id={this.props.component.id}
                        isRotating={this.props.isRotating}
                        relativeRotate={this.props.relativeRotate ? this.props.relativeRotate : 0}
                    />
                </LazyBoundary></div> : null;
    }

    private renderResizableOrResizableMobile() {
        return this.renderResizableMobile();
    }

    private shouldRenderRotateElement = () => {
        let shouldRender = this.props.canRotate
            && !this.props.isCroping
            && !this.props.isPreview;
        shouldRender = shouldRender && !this.props.isDragging && !this.props.isResizing;
        return shouldRender;
    };

    private shouldRenderRotateMobile() {
        const shouldRender = this.props.canRotate
            && !this.props.isCroping
            && !this.props.isPreview
            && !this.props.selectedElementInGroupInMobile
            && (this.props.isMobileRotatingMode || this.props.isRotating);
        return shouldRender;
    }

    private getDraggableMobile(rotateStyle) {
        return this.shouldRenderDraggable()
            ? <div style={rotateStyle}>
                <LazyBoundary>
                    <DraggableMobileNew
                        isDragging={this.props.isDragging}
                        isResizing={this.props.isResizing}
                        scale={this.props.scale}
                        getSize={this.props.getSize}
                        onDrag={this.props.onDrag}
                        onDragStart={this.props.onDragStart}
                        onDragStop={this.props.onDragStop}
                        relativeRotate={this.props.relativeRotate}
                        relativeFlipH={this.props.relativeFlipH}
                        relativeFlipV={this.props.relativeFlipV}
                    />
                </LazyBoundary>
            </div>
            : null;
    }

    private shouldRenderDraggable = () => this.props.draggable
        && this.props.isSelected
        && !this.props.isCroping
        && !this.props.selectedElementInGroupInMobile
        && !this.props.isMobileRotatingMode
        && !this.props.isPreview;

    private canResizeOrDrag() {
        return !this.props.isRotating && !this.props.isCroping;
    }

    // 空文本（无文字、无边框、无背景、非组合子元素）
    // eslint-disable-next-line complexity
    private isEmptyText() {
        const model = this.props.component;
        const isText = model.type === UIModelType.SHAPE;
        const { fillBackground, border } = model.props.style || {};
        const emptyPGL = isEditorEmpty((model as IShapeModel).paragraphList);
        const hasBorder = border?.color && border.color !== 'none';
        const hasBackground = fillBackground && Object.prototype.hasOwnProperty.call(fillBackground || {}, 'noFill')
            && fillBackground.noFill !== 1;
        const isNotGroupChild = model.parent?.type === UIModelType.SLIDE;
        return emptyPGL && !hasBorder && !hasBackground && isNotGroupChild && isText;
    }

    private renderMaskWrapper() {
        if (!this.shouldRenderMaskWrapper()) return null;
        const model = this.props.component;
        const scale = this.getScaleNum();
        let maskStyle;
        const rect = model.getFrame?.().getFrameInfo(!!model.operationFrame);
        if (model.type === UIModelType.GROUP) {
            maskStyle = { width: rect.width * scale, height: rect.height * scale,
                left: GroupUIEdge * scale, top: GroupUIEdge * scale };
        }
        if (model.type === UIModelType.TABLE) {
            const { width, height } = this.props.style;
            const mode = this.getEditingStateManager()?.resizeMode;
            const left = mode?.indexOf(MODES.LEFT) >= 0 ? model.props.style.width - rect.width : DEFAULT_SIZE;
            const top = mode?.indexOf(MODES.TOP) >= 0 ? model.props.style.height - rect.height : DEFAULT_SIZE;
            maskStyle = { width: width * scale, height: height * scale, left: left * scale, top: top * scale };
        }
        return <div style={{
            ...this.props.style,
            backgroundColor: MASK_COLOR,
            opacity: MASK_OPACITY,
            ...maskStyle,
        }} />;
    }

    /**
    * 目前组合和表格需要渲染蒙层
    */
    private shouldRenderMaskWrapper = () => {
        const model = this.props.component;
        return this.props.isResizing && model.operationFrame
            && ((model as IGroupModel).isRootGroup || model.type === UIModelType.TABLE);
    };

    private getScaleNum = () => this.props.scale ?? 1 / CANVAS_PT_SCALE;

    private getEditingStateManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.editingStateManager;
    }

    private getLinkManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.linkManager;
    }
}

export default CanvasElementMobile;
